import type {
    IDigitalDataPage,
    IDigitalDataComponent,
    IDigitalDataEvent,
    IDigitalData,
    IAnalyticsStrategy,
    IDigitalDataUser,
    IDigitalDataBaseEvent
} from '@nab/x-types';
import { sdkLoggerInterface as log } from '../../../Logger/Logger';

/**
 * Digital Data Strategy class,
 * The class will be used as the standard Analytics strategy by the App Loader for capturing any analytics information, of the Shell or container does not provide and specific Analytics strategy to be used
 * The basis of this strategy is that all existing channels that use the analytics base their analytics information capture on populating the **window.digitalData** object.
 * This object is exposed by the ensighten term library provided by the analytics team based on the configurations agreed with the consuming channel.
 * Digital data strategy uses this to help update the data on the **window.digitalData** object.
 */

class DigitalDataStrategy implements IAnalyticsStrategy {
    /**
     * This property determines if the Analytics manager is used in an application that already has the Tag Management System (TMS) initiated
     */
    private tmsEnabledEnv: boolean = false;

    private get digitalData(): IDigitalData {
        return (window as any).digitalData as IDigitalData;
    }

    private set digitalData(value: IDigitalData) {
        (window as any).digitalData = value;
    }

    /**
     * The property is the special case where the digitalData layer will allow miniapps to update the page properties.
     * This will be controlled by the shell setting the `customValue` of `allowPageOverride` on the `pageInfo` object.
     */
    private get IsPageUpdateAllowed(): boolean {
        return (
            this.digitalData &&
            (this.digitalData.page.pageInfo.customValues?.allowPageOverride === true ||
                this.digitalData.page.pageInfo.customValues?.allowPageOverride === 'enabled')
        );
    }

    constructor() {
        this.tmsEnabledEnv = !!this.digitalData;
        if (!this.tmsEnabledEnv) {
            this.initDefaultPage();
        }
    }

    private initDefaultPage() {
        this.digitalData = {
            page: {
                pageInfo: {
                    pageName: window.document.title,
                    site: window.location.href
                }
            },
            components: {},
            event: [],
            user: {}
        };
    }

    /**
     * Warning displayed if the environment is enabled with a Tag Management System (TMS)
     */
    private tmsWarning() {
        log.warn(
            `Tag Management System enabled in this env, the digitalData object is locked. 
            Please emit appropriate event using logEvent() method.`
        );
    }

    /**
     * If it is not a TMS system enabled system, this initializes the digitalData object
     * with the page information
     * @param {IDigitalDataPage} pageData Page object to be updated
     */
    public async initPage(pageData: IDigitalDataPage): Promise<boolean> {
        if (this.tmsEnabledEnv) {
            this.tmsWarning();
        } else {
            this.digitalData = {
                page: { pageInfo: pageData },
                components: {},
                event: []
            };
        }
        return Promise.resolve(true);
    }

    /**
     * If it is not a TMS system enabled system, this updates the digitalData object
     * with the changes to the page. These are useful in a SPA enabled with synthetic routing.
     * @param {IDigitalDataPage} pageData Page object to be updated
     */
    public async updatePage(pageData: Partial<IDigitalDataPage>): Promise<boolean> {
        if (this.tmsEnabledEnv && !this.IsPageUpdateAllowed) {
            this.tmsWarning();
        } else {
            const {
                page: { pageInfo }
            } = this.digitalData;
            // Retrieve the original value of page Override flag as set during the page load.
            const { customValues: { allowPageOverride } = {} } = pageInfo;

            this.digitalData.page.pageInfo = {
                ...pageInfo,
                ...pageData
            };

            // Once the new data is updated, reset the value of the page override to the original value,
            // This will avoid miniapps turning this feature off while updating the page, and keep the shells original settings.
            this.digitalData.page.pageInfo.customValues = {
                ...this.digitalData.page.pageInfo.customValues,
                allowPageOverride
            };
        }
        return Promise.resolve(true);
    }

    /**
     * Event logger used for logging data on the Analytics/Tag Management System (TMS)
     * @param {IDigitalDataEvent | IDigitalDataPageEvent} eventData details of the event being captured. It enforces the type should have eventType
     */
    public async logEvent<T extends IDigitalDataBaseEvent = IDigitalDataEvent>(eventData: T): Promise<boolean> {
        let { event } = this.digitalData;

        // if the event array is not initialized, creates and assigns an event array
        if (!event) {
            event = [];
            this.digitalData.event = event;
        }

        event.push(eventData as unknown as IDigitalDataEvent);

        return Promise.resolve(true);
    }

    /**
     * If it is not a TMS system enabled system, this updates the digitalData object
     * with the component loaded on to the page.
     * @param {IDigitalDataComponent} : Details of the component being loaded
     */
    public async logComponent({ data, componentName }: IDigitalDataComponent): Promise<boolean> {
        if (this.tmsEnabledEnv) {
            this.tmsWarning();
        } else {
            const { components } = this.digitalData;
            components[componentName] = data;
        }
        return Promise.resolve(true);
    }

    /**
     * If it is not a TMS system enabled system, this initialises the user data object
     * with the details provided of user.
     * @param {IDigitalDataUser} : Details of the user to be initalised with
     */
    public async initUser(userData: IDigitalDataUser): Promise<boolean> {
        if (this.tmsEnabledEnv) {
            this.tmsWarning();
        } else {
            this.digitalData.user = userData;
        }
        return Promise.resolve(true);
    }

    /**
     * If it is not a TMS system enabled system, this updates the user data object
     * with the details provided of user.
     * @param {IDigitalDataUser} : Details of the user to be updated
     */
    public async updateUser(userData: Partial<IDigitalDataUser>): Promise<boolean> {
        if (this.tmsEnabledEnv) {
            this.tmsWarning();
        } else {
            const { user } = this.digitalData;
            this.digitalData.user = { ...user, ...userData };
        }
        return Promise.resolve(true);
    }
}

export { DigitalDataStrategy };
